import * as actionTypes from "../constants/actionTypes";
import * as autotaskTypes from "../constants/autotaskTypes";
import * as componentTypes from "../constants/componentTypes";
import * as rcc from "../constants/rcc";
import * as rccHelper from "../helpers/rccReducerHelper";
import * as rccResponseHelper from "../helpers/rccResponseHelper";
import * as modelHelper from "../helpers/modelHelper";
import * as windowService from "../services/windowService";

import Request from "../models/utils/Request";
import Machine from "../models/utils/Machine";
import Printer from "../models/utils/Printer";
import RefillValidation from "../models/gui/RefillValidation";

const initialState = {
    url: windowService.getDefaultRcc(),
    connected: false,
    sequence: 0,
    requests: [],
    expected: [],
    dispensingFormula: null,
    machine: new Machine(1),
    printer: new Printer(""),
    activeCanister: null,
    canisterList: [],
    channelList: [],
    driverTypesList: [],
    defaultDriverConfiguration: null,
    driverConfigurationList: null,
    machineConfigurationList: null,
    printerList: [],
    templateList: [],
    unitList: [],
    autotaskList: [],
    smartPurgeRulesList: [],
    purgeStatistics: [],
    downloadingFileList: []
};

/**
 * Structure of control center store:
 * - url {Sring}
 * - connected {Boolean}                            - if true, client is connected to control center
 * - sequence {Number}                              - index for client - control center communication
 * - requests {Array}                               - array of control center requests in progres
 * - expected {Array}                               - array of control center asynchronous responses
 * - dispensingFormula {Object | null}
 * - machine {Object}                               - if machine.isConnected is true, rcc is connected to machine
 * - printer {Object}                               - default printer object
 * - activeCanister {Object | null}
 * - canisterList {Array}
 * - channelList {Array}
 * - driverTypesList {Array}
 * - defaultDriverConfiguration {Object | null}
 * - driverConfigurationList {Object | null}
 * - machineConfigurationList {Object | null}
 * - printerList {Array}
 * - templateList {Array}
 * - unitList {Array}
 * - autotaskList {Array}
 * - smartPurgeRulesList {Array}
 * - purgeStatistics {Array}
 * - downloadingFileList {Array}                    - list of downloading Files

 */
export const rccReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.RCC_WEBSOCKET_OPENED:
            return {
                ...state,
                connected: true
            };
        case actionTypes.RCC_WEBSOCKET_CLOSED:
            return {
                ...state,
                connected: false,
                requests: [],
                expected: [],
                dispensingFormula: null,
                machine: state.machine.reset(),
                printer: new Printer(""),
                activeCanister: null,
                canisterList: [],
                driverTypesList: [],
                defaultDriverConfiguration: null,
                driverConfigurationList: null,
                machineConfigurationList: null,
                printerList: [],
                templateList: [],
                unitList: []
            };
        case actionTypes.SAVE_RCC_WEBSOCKET_REQUEST:
            if (
                action.payload.method === undefined ||
                action.payload.tabId === undefined ||
                action.payload.next === undefined
            ) {
                return {
                    ...state,
                    sequence: action.payload.sequence
                };
            }

            return {
                ...state,
                sequence: action.payload.sequence,
                requests: state.requests.concat([
                    new Request(
                        action.payload.sequence,
                        action.payload.tabId,
                        action.payload.method,
                        action.payload.next
                    )
                ])
            };
        case actionTypes.REMOVE_RCC_WEBSOCKET_REQUEST:
            let filteredRequests = state.requests.filter((request) => request.id !== action.payload.sequence);

            return {
                ...state,
                requests: filteredRequests === undefined ? [] : filteredRequests
            };
        case actionTypes.SAVE_RCC_ASYNC_RESPONSE:
            if (
                action.payload.id === undefined ||
                action.payload.tabId === undefined ||
                action.payload.method === undefined
            ) {
                return state;
            }

            return {
                ...state,
                expected: state.expected.concat([
                    new Request(action.payload.id, action.payload.tabId, action.payload.method, action.payload.next)
                ])
            };
        case actionTypes.REMOVE_RCC_ASYNC_RESPONSE:
            let filteredExpRequests = state.expected.filter((response) => response.id !== action.payload.id);

            return {
                ...state,
                expected: filteredExpRequests === undefined ? [] : filteredExpRequests
            };
        case actionTypes.MACHINE_SET_SELECTION:
            if (action.payload.selected) {
                return {
                    ...state,
                    machine: state.machine.updateIsSelected(true)
                };
            }

            return {
                ...state,
                machine: state.machine.updateIsSelected(false),
                dispensingFormula: null,
                canisterList: [],
                channelList: []
            };
        case actionTypes.MACHINE_SET_CONNECTION:
            if (action.payload.connected) {
                return {
                    ...state,
                    machine: state.machine.updateIsConnected(true)
                };
            }

            let resetMachine = state.machine.reset();
            resetMachine.isSelected = true;

            return {
                ...state,
                machine: resetMachine,
                dispensingFormula: null,
                canisterList: [],
                channelList: []
            };
        case actionTypes.SAVE_MACHINE_CALLS:
            return {
                ...state,
                machine: state.machine.updateMethods(action.payload.methods)
            };
        case actionTypes.SAVE_AUTOTASK_TYPES:
            let type,
                types = [];
            for (type of autotaskTypes.ACTION_TYPES_SORTED) {
                if (action.payload.data.includes(type)) {
                    types.push(type);
                }
            }

            return {
                ...state,
                machine: state.machine.updateAutotaskTypes(types)
            };
        case actionTypes.SAVE_MACHINE_INFO:
            if (action.payload.data === null) {
                return state;
            }

            return {
                ...state,
                machine: state.machine.updateInfo(
                    action.payload.data.name !== undefined ? action.payload.data.name : "",
                    action.payload.data.serialNumber !== undefined ? action.payload.data.serialNumber : "",
                    action.payload.data.type !== undefined ? action.payload.data.type : null,
                    action.payload.data.typeName !== undefined ? action.payload.data.typeName : "",
                    action.payload.data.channels !== undefined ? action.payload.data.channels : 0,
                    action.payload.data.canisters !== undefined ? action.payload.data.canisters : 0
                )
            };
        case actionTypes.SAVE_MACHINE_DEVICES:
            return {
                ...state,
                machine: state.machine.updateDevices(action.payload.devices)
            };
        case actionTypes.SAVE_MACHINE_DRIVER_CONFIGURATION:
            let selectedDriver = rccHelper.parseDefaultDriverType(action.payload.configuration);

            return {
                ...state,
                machine: state.machine.updateTypeName(
                    selectedDriver === null ? rcc.DRIVER_TYPE_NONE : selectedDriver.type
                ),
                driverConfigurationList: rccHelper.parseDriverTypes(
                    action.payload.configuration,
                    state.driverTypesList
                ),
                defaultDriverConfiguration: selectedDriver
            };
        case actionTypes.SAVE_MACHINE_DRIVER_TYPES:
            return {
                ...state,
                driverTypesList: rccHelper.processList(action.payload.driverTypesList)
            };
        case actionTypes.SAVE_MACHINE_CONFIGURATION:
        case actionTypes.SAVE_MACHINE_CONFIGURATION_AND_RELOAD_CANISTERS:
            return {
                ...state,
                machineConfigurationList: action.payload.machineConfigurationList
            };
        case actionTypes.SAVE_MACHINE_CANISTERS:
            if (state.activeCanister === null) {
                return {
                    ...state,
                    canisterList: action.payload.canisterList
                };
            }

            let activeItem = action.payload.canisterList.find((item) => item.id === state.activeCanister.id);

            return {
                ...state,
                canisterList: action.payload.canisterList,
                activeCanister: activeItem !== undefined ? state.activeCanister.updateData(activeItem) : null
            };
        case actionTypes.SAVE_MACHINE_CHANNELS:
            return {
                ...state,
                channelList: rccHelper.processChannelList(action.payload.data)
            };
        case actionTypes.MACHINE_SET_BUSY:
            return {
                ...state,
                machine: state.machine.updateIsBusy(action.payload.isBusy)
            };
        case actionTypes.SAVE_RCC_DIALOG:
            if (action.payload.dialog.type === componentTypes.MACHINE_DIALOG_REFILL) {
                let refillCanister = rccResponseHelper.getCanisterByRefillDialog(
                    action.payload.dialog,
                    state.canisterList
                );

                if (refillCanister !== null) {
                    return {
                        ...state,
                        machine: state.machine.updateDialogAndValidation(
                            action.payload.dialog,
                            new RefillValidation(
                                refillCanister.id,
                                state.machineConfigurationList !== null &&
                                state.machineConfigurationList[rcc.CONFIG_BARCODE_REFILL_COLORANT_CHECK]
                                    ? refillCanister
                                    : null
                            )
                        )
                    };
                }
            }

            return {
                ...state,
                machine: state.machine.updateDialog(action.payload.dialog)
            };
        case actionTypes.CANCEL_RCC_DIALOG:
            return {
                ...state,
                machine: state.machine.updateDialog(null).updateProductImageId(-1)
            };
        case actionTypes.SAVE_RCC_DIALOG_PROGRESS:
            if (state.machine.dialog === null) {
                return state;
            }

            let itemsDone = 0;
            for (let item of action.payload.progressItems) {
                if (item.done) {
                    itemsDone++;
                }
            }
            return {
                ...state,
                machine: state.machine.updateDialogProgress(
                    action.payload.percentage,
                    action.payload.timeLeft,
                    action.payload.progressItems,
                    action.payload.progressItems.length,
                    itemsDone
                )
            };
        case actionTypes.MACHINE_SAVE_VALIDATION_RESULT:
            return {
                ...state,
                canisterList: rccHelper.updateCanisterList(state.canisterList, action.payload.result)
            };
        case actionTypes.SAVE_DISPENSING_FORMULA:
            if (state.dispensingFormula !== null) {
                return state;
            }

            return {
                ...state,
                dispensingFormula: {
                    formula: action.payload.formula,
                    base: action.payload.base,
                    moreInfoText: action.payload.moreInfoText,
                    dispenseNote: action.payload.dispenseNote,
                    moreInfoInternal: action.payload.moreInfoInternal,
                    buildingSite: action.payload.buildingSite,
                    priceList: action.payload.priceList,
                    infoData: action.payload.infoData,
                    printData: action.payload.printData,
                    tmcData: action.payload.tmcData
                }
            };
        case actionTypes.SAVE_FORMULA_META_AND_PRINT_DATA:
            if (
                state.dispensingFormula === null ||
                state.dispensingFormula.formula === null ||
                action.payload.tabId !== componentTypes.QUICK_TINT_TAB_ID
            ) {
                return state;
            }

            return {
                ...state,
                dispensingFormula: {
                    ...state.dispensingFormula,
                    priceList: action.payload.priceList,
                    infoData: action.payload.infoData,
                    printData: modelHelper.formatPrintData(action.payload.printData),
                    tmcData: action.payload.tmcData
                }
            };
        case actionTypes.RELOAD_DISPENSING_FORMULA_META_DATA:
            if (state.dispensingFormula === null || state.dispensingFormula.formula === null) {
                return state;
            }

            return {
                ...state,
                dispensingFormula: {
                    ...state.dispensingFormula,
                    priceList: action.payload.priceList,
                    infoData: action.payload.infoData,
                    printData: modelHelper.formatPrintData(action.payload.printData),
                    tmcData: action.payload.tmcData
                }
            };
        case actionTypes.POP_DISPENSING_FORMULA:
            return {
                ...state,
                dispensingFormula: null
            };
        case actionTypes.SAVE_MACHINE_ERRORS:
            return {
                ...state,
                machine: state.machine.updateErrors(action.payload.errors)
            };
        case actionTypes.SAVE_MACHINE_HISTORY_ACTIONS:
            return {
                ...state,
                machine: state.machine.updateHistoryActions(action.payload.historyActions)
            };
        case actionTypes.RCC_SAVE_URL:
            return {
                ...state,
                url: action.payload.url
            };
        case actionTypes.SAVE_DISPENSING_PACKAGE_IMAGE:
            return {
                ...state,
                machine: state.machine.updateProductImageId(action.payload.imageId)
            };
        case actionTypes.UPDATE_MACHINE_SENSOR:
            return {
                ...state,
                machine: state.machine.updateSensors(rccHelper.processSensorList(action.payload.update))
            };
        case actionTypes.MACHINE_SAVE_VALIDATION_CANISTER:
            return {
                ...state,
                machine: state.machine.updateValidationCanister(modelHelper.buildCanisterObject(action.payload.result))
            };
        case actionTypes.MACHINE_SAVE_VALIDATION_COLORANT_PACKAGE:
            if (action.payload.result === null) {
                return {
                    ...state,
                    machine: state.machine.updateValidationColorantPackage(modelHelper.buildColorantPackageNullObject())
                };
            }

            return {
                ...state,
                machine: state.machine.updateValidationColorantPackage(
                    modelHelper.buildColorantPackageObject(action.payload.result)
                )
            };
        case actionTypes.MACHINE_UPDATE_REFILL_VALIDATION_WIZARD:
            return {
                ...state,
                machine: state.machine.updateValidationCanisterIndex(action.payload.index)
            };
        case actionTypes.MACHINE_RESET_REFILL_VALIDATION:
            if (
                state.machineConfigurationList !== null &&
                state.machineConfigurationList[rcc.CONFIG_BARCODE_REFILL_COLORANT_CHECK]
            ) {
                return {
                    ...state,
                    machine: state.machine.resetColorantPackageValidation()
                };
            }

            return {
                ...state,
                machine: state.machine.resetValidation()
            };
        case actionTypes.SAVE_DEFAULT_PRINTER:
            return {
                ...state,
                printer: state.printer.updateName(action.payload.name)
            };
        case actionTypes.SAVE_PRINTERS_LIST:
            return {
                ...state,
                printerList: modelHelper.mapPrintersToObjects(action.payload.printerList)
            };
        case actionTypes.SAVE_TEMPLATES_LIST:
            return {
                ...state,
                templateList: modelHelper.mapTemplatesToObjects(action.payload.templateList)
            };
        case actionTypes.SAVE_MACHINE_UNITS:
            return {
                ...state,
                unitList: action.payload.unitList
            };
        case actionTypes.SAVE_AUTOTASK_LIST:
            return {
                ...state,
                autotaskList: modelHelper.mapAutotasksToObjects(action.payload.autotaskList)
            };
        case actionTypes.SAVE_SMART_PURGE_RULES_LIST:
            return {
                ...state,
                smartPurgeRulesList: modelHelper.mapSmartPurgeRulesToObjects(
                    action.payload.smartPurgeRulesList,
                    state.channelList
                )
            };
        case actionTypes.UPDATE_FORMULA_DISPENSING_INFO:
            return {
                ...state,
                machine: state.machine.updateIsDispensing(action.payload.dispensing)
            };
        case actionTypes.SAVE_MACHINE_DIALOG_TEXT:
            if (state.machine.dialog === null) {
                return state;
            }

            return {
                ...state,
                machine: state.machine.updateDialogText(action.payload.text)
            };
        case actionTypes.SAVE_PURGE_STATS:
            return {
                ...state,
                purgeStatistics: modelHelper.mapPurgeStatisticsToObjects(action.payload.purgeStats)
            };
        case actionTypes.SAVE_ACTIVE_CANISTER:
            let activeCanister = state.canisterList.find((canister) => canister.id === action.payload.canister.id);

            if (activeCanister === undefined) {
                return state;
            }

            return {
                ...state,
                activeCanister: activeCanister
            };
        case actionTypes.SAVE_PACKAGES_FOR_COLORANT:
            let update = null;

            if (state.machine.dialog !== null && state.machine.dialog.type === componentTypes.MACHINE_DIALOG_REFILL) {
                update = rccResponseHelper.getCanisterByRefillDialog(state.machine.dialog, state.canisterList);
            }

            if (update === null) {
                update = state.activeCanister;
            }

            if (update === null) {
                return state;
            }

            let colorantPackages = modelHelper.mapColorantPackagesToObjects(action.payload.colorantPackages);

            return {
                ...state,
                activeCanister: update.updateColorantPackages(colorantPackages),
                canisterList: state.canisterList.map((canister) => {
                    if (canister.id === update.id) {
                        return canister.updateColorantPackages(colorantPackages);
                    }

                    return canister;
                })
            };
        case actionTypes.CLOSE_ACTIVE_TAB:
        case actionTypes.CLOSE_ALL_TABS:
            if (!action.payload.newTabList.some((item) => item.type === componentTypes.CANISTERS_TAB)) {
                return {
                    ...state,
                    activeCanister: null
                };
            }

            return state;
        case actionTypes.RCC_SAVE_FILE_INFORMATION:
            return {
                ...state,
                downloadingFileList: state.downloadingFileList.concat(action.payload.downloadingFile)
            };
        case actionTypes.RCC_UPDATE_FILE_INFORMATION:
            return {
                ...state,
                downloadingFileList: state.downloadingFileList.map((item) =>
                    item.id === action.payload.downloadingFile.id ? action.payload.downloadingFile : item
                )
            };
        case actionTypes.RCC_DELETE_DOWNLOADED_FILE:
            return {
                ...state,
                downloadingFileList: state.downloadingFileList.filter(
                    (item) => item.id !== action.payload.downloadedFileId
                )
            };
        default:
            return state;
    }
};
